import { Comment, CommentAnchorType } from '@marketing-milk/interfaces'
import { SECTIONS, SectionKey } from 'pages/business/sprint-packets/editor/sections'
import { unresolvedCountKey } from './commentCounts'

/**
 * Per-packet "All comments" grouping (v1.4b).
 *
 * Derives an ordered, anchor-grouped view of the already-hydrated packet comments
 * (`SprintPacketHydrated.comments`) for the AllCommentsDrawer — no endpoint. Pure
 * and DOM-free: ordering, section mapping, root-only grouping, and counts all live
 * here so the drawer does no list logic of its own. Both host navigation paths
 * (Edit scroll, Present slide jump) resolve an anchor's owning section via
 * {@link anchorSectionKey}.
 */

/**
 * The section a comment anchor belongs to. Section-family anchors
 * (`section`/`narrative-block`) parse `<packetID>:<sectionKey>`; entity anchors map
 * to their owning section so creative/audience comments still group + navigate to a
 * real slide. Returns null when a section-family key isn't a known SECTION.
 */
export const anchorSectionKey = (
  anchorType: CommentAnchorType,
  anchorID: string
): SectionKey | null => {
  switch (anchorType) {
    case 'section':
    case 'narrative-block': {
      const key = anchorID.slice(anchorID.indexOf(':') + 1)
      return SECTIONS.some(s => s.key === key) ? (key as SectionKey) : null
    }
    case 'performance-metric':
      return 'performance-review'
    case 'creative':
      return 'creative'
    case 'audience':
      return 'audience-framework'
    default:
      return null
  }
}

export interface AnchorGroup {
  anchorType: CommentAnchorType
  anchorID: string
  /** `unresolvedCountKey(type, id)` — stable group identity. */
  key: string
  /** Owning section via {@link anchorSectionKey} (null for unmappable section keys). */
  sectionKey: SectionKey | null
  /** True for `creative`/`audience` — the location filter's "Entities" bucket. */
  isEntity: boolean
  /** Root comments only (`parentCommentID == null`), newest-first. */
  rootComments: Comment[]
  /** Root-comment count (mirrors badge semantics — replies never inflate it). */
  total: number
  /** Unresolved root-comment count. */
  unresolved: number
}

const isRoot = (c: Comment): boolean =>
  c.parentCommentID === null || c.parentCommentID === undefined

const isUnresolvedRoot = (c: Comment): boolean => isRoot(c) && !c.resolved

const ENTITY_TYPES: CommentAnchorType[] = ['creative', 'audience']

const sectionOrder = (key: SectionKey | null): number => {
  const idx = key === null ? -1 : SECTIONS.findIndex(s => s.key === key)
  // Unmappable section-family anchors sort with the section family, at the end.
  return idx < 0 ? SECTIONS.length : idx
}

// Entity anchors order after the section family: creatives before audiences, then
// by numeric id so the list reads in a stable packet order.
const entityRank = (type: CommentAnchorType): number => (type === 'creative' ? 0 : 1)

const numericId = (id: string): number => {
  const n = Number(id)
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER
}

/**
 * Group a flat comment list by anchor into ordered, status-ready rows. Root
 * comments only (replies are never standalone rows and never inflate counts,
 * mirroring `CommentThread`'s root/reply split and `commentCounts`' badge
 * semantics). Groups with zero root comments are skipped. Order: section-family
 * anchors in `SECTIONS` order, then entity anchors (creatives, then audiences) by
 * numeric id — so the list reads top-to-bottom in packet order.
 */
export const groupCommentsByAnchor = (comments: Comment[]): AnchorGroup[] => {
  const byKey: Record<string, AnchorGroup> = {}
  const order: AnchorGroup[] = []

  for (const c of comments) {
    if (!isRoot(c)) continue
    const key = unresolvedCountKey(c.anchorType, c.anchorID)
    let group = byKey[key]
    if (!group) {
      group = {
        anchorType: c.anchorType,
        anchorID: c.anchorID,
        key,
        sectionKey: anchorSectionKey(c.anchorType, c.anchorID),
        isEntity: ENTITY_TYPES.includes(c.anchorType),
        rootComments: [],
        total: 0,
        unresolved: 0,
      }
      byKey[key] = group
      order.push(group)
    }
    group.rootComments.push(c)
    group.total += 1
    if (isUnresolvedRoot(c)) group.unresolved += 1
  }

  order.forEach(group => {
    // Newest-first within a group.
    group.rootComments.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  })

  return order.sort((a, b) => {
    if (a.isEntity !== b.isEntity) return a.isEntity ? 1 : -1
    if (!a.isEntity && !b.isEntity) return sectionOrder(a.sectionKey) - sectionOrder(b.sectionKey)
    // Both entities: creatives before audiences, then by numeric id.
    if (a.anchorType !== b.anchorType) return entityRank(a.anchorType) - entityRank(b.anchorType)
    return numericId(a.anchorID) - numericId(b.anchorID)
  })
}
